import { apiService } from './api';

interface StreamStartParams {
  sessionId: string;
  gameId: string;
  streamingPlatform: string;
  channelId: string;
  streamerName?: string;
  streamInstanceId?: string;
  streamUrl?: string;
  streamInfo?: Record<string, any>;
  streamSettings?: Record<string, any>;
  shareToDiscord?: boolean;
}

interface StreamMetricsUpdate {
  viewerCount?: number;
  likeCount?: number;
  commentCount?: number;
  duration?: number;
}

/**
 * Webplay Stream Service
 * Handles stream session tracking for webplay when users connect to streaming platforms
 */
class WebplayStreamService {
  private currentStreamId: string | null = null;
  private streamStartTime: number = 0;
  private updateThrottleMs: number = 30000; // 30 seconds
  private lastUpdateTime: number = 0;
  private accumulatedMetrics: StreamMetricsUpdate = {};
  private isFirstUpdate: boolean = true;

  /**
   * Start a new stream session
   */
  async startStream(params: StreamStartParams): Promise<string | null> {
    try {
      // Generate stream instance ID if not provided
      const streamInstanceId = params.streamInstanceId || 
        `stream_${params.channelId}_${Date.now()}`;

      console.log('📡 [WebplayStream] Starting stream session:', {
        platform: params.streamingPlatform,
        gameId: params.gameId,
        sessionId: params.sessionId,
        streamInstanceId
      });

      if (!params.sessionId) {
        console.error('❌ [WebplayStream] Cannot start stream without sessionId');
        throw new Error('Session ID is required for stream logging');
      }

      // Skip stream logging for temporary offline sessions (they can't be linked to server data)
      if (params.sessionId.startsWith('temp-')) {
        console.log('📊 [WebplayStream] Skipping stream logging for temporary offline session');
        throw new Error('Cannot log streams for temporary offline sessions');
      }

      const response = await apiService.post('/webplay/stream/started', {
        ...params,
        streamInstanceId
      });

      if (response.data.success && response.data.data?.streamLogId) {
        this.currentStreamId = response.data.data.streamLogId;
        this.streamStartTime = Date.now();
        this.lastUpdateTime = Date.now();
        this.accumulatedMetrics = {};
        this.isFirstUpdate = true; // Reset flag for new stream

        console.log('✅ [WebplayStream] Stream session started:', this.currentStreamId);
        return this.currentStreamId;
      } else {
        throw new Error('Invalid response from stream start endpoint');
      }
    } catch (error) {
      console.error('❌ [WebplayStream] Failed to start stream session:', error);
      throw error;
    }
  }

  /**
   * Update stream metrics (throttled)
   */
  async updateMetrics(metrics: StreamMetricsUpdate): Promise<void> {
    if (!this.currentStreamId) {
      console.warn('⚠️ [WebplayStream] No active stream session for metrics update');
      return;
    }

    console.log('🔍 [WebplayStream] Received metrics:', {
      viewerCount: metrics.viewerCount,
      likeCount: metrics.likeCount,
      isFirstUpdate: this.isFirstUpdate,
      timeSinceLastUpdate: Date.now() - this.lastUpdateTime
    });

    // Update accumulated metrics
    if (metrics.viewerCount !== undefined) {
      this.accumulatedMetrics.viewerCount = metrics.viewerCount;
    }
    if (metrics.likeCount !== undefined) {
      this.accumulatedMetrics.likeCount = metrics.likeCount;
    }
    if (metrics.commentCount !== undefined) {
      this.accumulatedMetrics.commentCount = metrics.commentCount;
    }

    // Check if we have any actual metrics to send
    const hasMetrics = this.accumulatedMetrics.viewerCount !== undefined || 
                       this.accumulatedMetrics.likeCount !== undefined ||
                       this.accumulatedMetrics.commentCount !== undefined;

    if (!hasMetrics) {
      console.log('⏸️ [WebplayStream] No metrics to send yet');
      return;
    }

    // Force immediate update for first metrics WITH DATA, then throttle subsequent updates
    const now = Date.now();
    const shouldUpdate = this.isFirstUpdate || (now - this.lastUpdateTime >= this.updateThrottleMs);
    
    console.log('🔍 [WebplayStream] Should update?', {
      shouldUpdate,
      isFirstUpdate: this.isFirstUpdate,
      throttleCheck: now - this.lastUpdateTime >= this.updateThrottleMs,
      hasMetrics
    });
    
    if (!shouldUpdate) {
      console.log('⏸️ [WebplayStream] Metrics update throttled');
      return;
    }

    try {
      const duration = Math.floor((now - this.streamStartTime) / 1000); // in seconds
      
      console.log(`📊 [WebplayStream] ${this.isFirstUpdate ? 'Initial' : 'Updating'} stream metrics:`, {
        streamId: this.currentStreamId,
        viewers: this.accumulatedMetrics.viewerCount,
        likes: this.accumulatedMetrics.likeCount,
        isFirst: this.isFirstUpdate
      });

      await apiService.post('/webplay/stream/update', {
        streamLogId: this.currentStreamId,
        viewerCount: this.accumulatedMetrics.viewerCount,
        likeCount: this.accumulatedMetrics.likeCount,
        commentCount: this.accumulatedMetrics.commentCount
      });

      this.lastUpdateTime = now;
      this.isFirstUpdate = false; // Mark that we've sent the first update

      console.log('✅ [WebplayStream] Metrics updated successfully');
    } catch (error) {
      console.error('❌ [WebplayStream] Failed to update metrics:', error);
    }
  }

  /**
   * End the current stream session
   */
  async endStream(): Promise<void> {
    if (!this.currentStreamId) {
      console.warn('⚠️ [WebplayStream] No active stream session to end');
      return;
    }

    try {
      console.log('🏁 [WebplayStream] Ending stream session:', this.currentStreamId);

      const duration = Math.floor((Date.now() - this.streamStartTime) / 1000); // in seconds

      await apiService.post('/webplay/stream/ended', {
        streamLogId: this.currentStreamId,
        endReason: 'manual'
      });

      console.log('✅ [WebplayStream] Stream session ended');
      
      this.currentStreamId = null;
      this.accumulatedMetrics = {};
      this.isFirstUpdate = true; // Reset for next stream
    } catch (error) {
      console.error('❌ [WebplayStream] Failed to end stream session:', error);
      this.currentStreamId = null;
      this.accumulatedMetrics = {};
      this.isFirstUpdate = true; // Reset for next stream
    }
  }

  /**
   * Get current stream ID
   */
  getCurrentStreamId(): string | null {
    return this.currentStreamId;
  }

  /**
   * Check if there's an active stream session
   */
  hasActiveStream(): boolean {
    return this.currentStreamId !== null;
  }
}

// Export singleton instance
export const webplayStreamService = new WebplayStreamService();
export default webplayStreamService;
